// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isEqual = (value: any, other: any): boolean => {
  if (Object.is(value, other)) {
    return true;
  }

  if (value == null || other == null || typeof value !== typeof other) {
    return value === other;
  }

  if (value instanceof Date && other instanceof Date) {
    return value.getTime() === other.getTime();
  }

  if (typeof value !== 'object') {
    return value === other;
  }

  if (Array.isArray(value) !== Array.isArray(other)) {
    return false;
  }

  if (Array.isArray(value)) {
    return value.length === other.length && value.every((item, index) => isEqual(item, other[index]));
  }

  const keys = Object.keys(value);
  return keys.length === Object.keys(other).length && keys.every(key => isEqual(value[key], other[key]));
};

export const clone = <T>(value: T): T => {
  if (Array.isArray(value)) {
    return value.slice() as T;
  }

  if (value !== null && typeof value === 'object') {
    return { ...(value as object) } as T;
  }

  return value;
};

const cloneDeepWithSeen = <T>(value: T, seen: WeakMap<object, unknown>): T => {
  if (value === null || typeof value !== 'object') {
    return value;
  }

  if (seen.has(value)) {
    return seen.get(value) as T;
  }

  if (value instanceof Date) {
    const date = new Date(value.getTime());
    seen.set(value, date);
    return date as T;
  }

  if (Array.isArray(value)) {
    const result: unknown[] = [];
    seen.set(value, result);
    value.forEach(item => {
      result.push(cloneDeepWithSeen(item, seen));
    });
    return result as T;
  }

  const result = Object.create(Object.getPrototypeOf(value));
  seen.set(value as object, result);

  Object.keys(value as object).forEach(key => {
    result[key] = cloneDeepWithSeen((value as Record<string, unknown>)[key], seen);
  });

  return result;
};

export const cloneDeep = <T>(value: T): T => cloneDeepWithSeen(value, new WeakMap());
